import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds. Prevents refetching when
      // components remount quickly (e.g., navigating between arche tabs)
      // while keeping data reasonably fresh for a collaborative platform.
      staleTime: 30_000,

      // Unused cache entries are garbage collected after 5 minutes.
      // Gives users time to navigate back and see cached data while
      // a background refetch happens.
      gcTime: 5 * 60 * 1000,

      // Retry once for server/network errors, never for client errors (4xx).
      retry: (failureCount, error) => {
        if (error instanceof Error && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 1;
      },

      // Refetch when the browser tab regains focus — important for a
      // collaborative platform where data may change while the user
      // is on another tab.
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  },
});

/**
 * A page restored from the browser's back-forward cache comes back with the
 * data it was left with, however long ago. Focus or visibility events may not
 * accompany the restore, so refetch everything on screen explicitly — the page
 * keeps its place, its data is current. (iPhone and iPad step back to an
 * arche's page this way: "Keeping the page an arche leaves" in page-manifest.ts.)
 */
export function refetchOnBackForwardRestore(event: Pick<PageTransitionEvent, 'persisted'>): void {
  if (event.persisted) void queryClient.invalidateQueries();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', refetchOnBackForwardRestore);
}
